import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Tooltip } from '@mui/material'
import { components as reactSelectComponents } from 'react-select'
import { wrapMenuList } from 'react-select-async-paginate'
import { getCPDataWithoutParams } from 'src/clinic/services'
import { getIntelSuggestions } from 'src/emr/intel/prescriptionIntel'
import { IntelSourceChip } from 'src/emr/intel/IntelSourceChip'
import {
  addOrUpdateClinicalEntitiesInIdb,
  clinicalEntityStoreHasData,
  searchClinicalEntitiesInIdb,
} from 'src/emr/database/indexedDbDao'

/**
 * Shared ICD-10 support for the clinical-entity autocompletes (clinical-diagnosis /
 * clinical-symptom). The backend serves WHO ICD-10 2019 docs with `icd10Code` and an embedded
 * `icdClassification` {chapterCode, chapterTitle, blockCode, blockTitle, parentCode, leaf,
 * usage, inclusionTerms, exclusions, codingHints} — see javaserver docs/ICD10_IMPORT.md.
 *
 * Clinic-curated custom entries carry none of these fields and must keep working everywhere,
 * so every helper here degrades to a plain title when the ICD fields are absent.
 */

const ICD_PAGE_SIZE = 10

const asArray = (value) => (Array.isArray(value) ? value : value ? [value] : [])

/** Small code badge rendered next to the entity title, e.g. [F32.1] */
export const IcdCodeChip = ({ code, style }) => {
  if (!code) return null
  return (
    <span
      style={{
        display: 'inline-block',
        fontFamily: "'SF Mono', 'Roboto Mono', Menlo, monospace",
        fontSize: '11px',
        fontWeight: 700,
        lineHeight: '16px',
        color: '#4338ca',
        backgroundColor: 'rgba(99, 102, 241, 0.12)',
        border: '1px solid rgba(99, 102, 241, 0.3)',
        borderRadius: '4px',
        padding: '0 5px',
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
        flexShrink: 0,
        ...style,
      }}
    >
      {code}
    </span>
  )
}

export function hasIcdDetail(entity) {
  const cls = entity?.icdClassification
  return Boolean(
    entity?.description ||
      asArray(cls?.exclusions).length ||
      asArray(cls?.inclusionTerms).length ||
      asArray(cls?.codingHints).length,
  )
}

const tooltipSectionStyle = { marginTop: '6px' }

function IcdDetailContent({ entity }) {
  const cls = entity?.icdClassification || {}
  const title = entity?.title || entity?.label || entity?.value || ''
  const exclusions = asArray(cls?.exclusions)
  const inclusionTerms = asArray(cls?.inclusionTerms)
  const codingHints = asArray(cls?.codingHints)
  return (
    <div style={{ maxWidth: '340px', fontSize: '12.5px', lineHeight: 1.45 }}>
      <div style={{ fontWeight: 700 }}>
        {entity?.icd10Code ? `${entity.icd10Code} · ` : ''}
        {title}
      </div>
      {(cls?.chapterTitle || cls?.blockTitle) && (
        <div style={{ opacity: 0.8, marginTop: '2px' }}>
          {[cls?.chapterTitle, cls?.blockCode && cls?.blockTitle ? `${cls.blockCode} ${cls.blockTitle}` : cls?.blockTitle]
            .filter(Boolean)
            .join(' → ')}
        </div>
      )}
      {entity?.description && <div style={tooltipSectionStyle}>{entity.description}</div>}
      {inclusionTerms.length > 0 && (
        <div style={tooltipSectionStyle}>
          <b>Includes:</b> {inclusionTerms.join('; ')}
        </div>
      )}
      {exclusions.length > 0 && (
        <div style={tooltipSectionStyle}>
          <b>Excludes:</b> {exclusions.join('; ')}
        </div>
      )}
      {codingHints.length > 0 && (
        <div style={tooltipSectionStyle}>
          <b>Coding:</b> {codingHints.join('; ')}
        </div>
      )}
    </div>
  )
}

/** Hover affordance: wraps children in a tooltip with the WHO definition + exclusions. */
export const IcdDetailTooltip = ({ entity, placement = 'right', children }) => {
  if (!hasIcdDetail(entity)) {
    return children
  }
  return (
    <Tooltip
      title={<IcdDetailContent entity={entity} />}
      placement={placement}
      enterDelay={350}
      // the select menus render in a body portal at zIndex 9999-10000; stay above them
      slotProps={{ popper: { style: { zIndex: 10500 } } }}
    >
      {children}
    </Tooltip>
  )
}

function highlightTextMatch(label, inputValue) {
  if (!inputValue) return label
  const matchIndex = String(label).toLowerCase().indexOf(inputValue.toLowerCase())
  if (matchIndex === -1) return label
  return (
    <span>
      {label.slice(0, matchIndex)}
      <span
        style={{
          fontWeight: 700,
          color: '#4338ca',
          backgroundColor: 'rgba(67, 56, 202, 0.1)',
          padding: '1px 3px',
          borderRadius: '3px',
        }}
      >
        {label.slice(matchIndex, matchIndex + inputValue.length)}
      </span>
      {label.slice(matchIndex + inputValue.length)}
    </span>
  )
}

/**
 * react-select formatOptionLabel for clinical entities: title (search-highlighted in the menu)
 * + ICD code chip, with the WHO definition/exclusions on hover where available.
 */
export const formatIcdOptionLabel = (option, { context, inputValue } = {}) => {
  const label = option?.label || option?.title || option?.value || ''
  if (option?.__isNew__) {
    return label
  }

  if (context === 'value') {
    return (
      <IcdDetailTooltip entity={option} placement="top">
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', minWidth: 0 }}>
          <span>{label}</span>
          <IcdCodeChip code={option?.icd10Code} />
        </span>
      </IcdDetailTooltip>
    )
  }

  return (
    <IcdDetailTooltip entity={option}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
        <span style={{ flex: 1, minWidth: 0 }}>{highlightTextMatch(label, inputValue)}</span>
        {/* most-used / diagnosis-context tag for the "★ Frequently used" group items */}
        {option?.__intelSource && <IntelSourceChip source={option.__intelSource} />}
        <IcdCodeChip code={option?.icd10Code} />
        {hasIcdDetail(option) && (
          <InfoOutlinedIcon sx={{ fontSize: 15, color: 'rgba(99, 102, 241, 0.7)', flexShrink: 0 }} />
        )}
      </div>
    </IcdDetailTooltip>
  )
}

/** Groups a flat option list into react-select groups by ICD block (section headers). */
export function groupOptionsByIcdBlock(options) {
  const groups = []
  const groupIndexByLabel = new Map()
  asArray(options).forEach((option) => {
    const cls = option?.icdClassification
    const groupLabel = cls?.blockTitle
      ? [cls?.blockCode, cls.blockTitle].filter(Boolean).join(' · ')
      : cls?.chapterTitle || 'Clinic & custom entries'
    if (!groupIndexByLabel.has(groupLabel)) {
      groupIndexByLabel.set(groupLabel, groups.length)
      groups.push({ label: groupLabel, options: [] })
    }
    groups[groupIndexByLabel.get(groupLabel)].options.push(option)
  })
  return groups
}

/**
 * Client-side mirror of the backend SpecializationSlugs bridge (practitioner noun
 * "psychiatrist" -> discipline slug "psychiatry") — needed because IndexedDB searches
 * can't lean on the server's normalization. Unknown slugs pass through unchanged;
 * "default" means "no filter". Keep in sync with javaserver SpecializationSlugs.
 */
const PRACTITIONER_TO_DISCIPLINES = {
  psychiatrist: ['psychiatry'],
  'therapist_clinical-psychologist': ['psychology'],
  'clinical-psychologist': ['psychology'],
  therapist: ['psychology'],
  psychologist: ['psychology'],
  neurologist: ['neurology'],
  rheumatologist: ['rheumatology'],
  pediatrician: ['pediatrics'],
  neonatologist: ['neonatology', 'pediatrics'],
  endocrinologist: ['endocrinology'],
  nutritionist: ['nutrition', 'endocrinology'],
  dietitian: ['nutrition'],
  'internal-medicine': ['general-medicine'],
  'general-physician': ['general-medicine'],
  cardiologist: ['cardiology'],
  dermatologist: ['dermatology'],
  gynecologist: ['gynecology', 'obstetrics-gynecology'],
  obstetrician: ['obstetrics-gynecology'],
  ophthalmologist: ['ophthalmology'],
  otolaryngologist: ['otolaryngology'],
  orthopedist: ['orthopedics'],
  oncologist: ['oncology'],
  hematologist: ['hematology'],
  pulmonologist: ['pulmonology'],
  gastroenterologist: ['gastroenterology'],
  nephrologist: ['nephrology'],
  urologist: ['urology'],
}

export function resolveToDisciplineSlugs(specializationId) {
  const normalized = (specializationId || '').trim().toLowerCase()
  if (!normalized || normalized === 'default') return []
  return PRACTITIONER_TO_DISCIPLINES[normalized] || [normalized]
}

function toSelectOption(entity) {
  return { ...entity, value: entity?.id || entity?.title, label: entity?.title }
}

async function fetchClinicalEntitiesFromApi(entityApiParam, search, additional, page, pageSize) {
  const params = new URLSearchParams()
  params.set('search', search || '')
  if (additional?.clinicId) params.set('clinicId', additional.clinicId)
  if (additional?.clinicBrandId) params.set('clinicBrandId', additional.clinicBrandId)
  if (additional?.specializationId) params.set('specializationId', additional.specializationId)
  if (additional?.leafOnly) params.set('leafOnly', 'true')
  params.set('page', page)
  params.set('pageSize', pageSize)

  const response = await getCPDataWithoutParams(
    `/api/secure/clinic-practitioner/${entityApiParam}/list?${params.toString()}`,
  )
  if (response?.status === 200 && Array.isArray(response.data)) {
    return response.data
  }
  return null
}

// Bounded wait for the server half of a merged search — a slow/offline network never
// blocks the dropdown; late responses still write through to IndexedDB for next time.
const CLINICAL_SERVER_MERGE_TIMEOUT_MS = 1200

function collectLoadedOptionIds(loadedOptions) {
  const ids = new Set()
  ;(loadedOptions || []).forEach((entry) => {
    const options = Array.isArray(entry?.options) ? entry.options : [entry]
    options.forEach((option) => {
      if (option?.id) ids.add(option.id)
    })
  })
  return ids
}

/**
 * loadOptions factory for react-select-async-paginate against the clinical entity list
 * endpoints (`clinical-diagnosis`, `clinical-symptom`, `clinical-descriptor`).
 *
 * Local + server MERGED on every page: the IndexedDB store (filled by the background sync,
 * with specializationId/leafOnly applied client-side using the server's semantics) answers
 * instantly, and the same page is requested from the server in parallel — bounded by a
 * timeout — so a partially-synced store never hides server entries. Results are deduped
 * against each other and against already-loaded pages, block-grouped for the menu — pair
 * with reduceGroupedOptions on the select.
 */
export function makeClinicalEntityLoadOptions(entityApiParam) {
  return async (search, loadedOptions, additional) => {
    const page = additional?.page || 1

    // blank first open: lead with the practitioner's most-used / diagnosis-context
    // picks of this clinical type (from the intel cache; instant when primed) as a
    // dedicated group above the ICD-block results
    let intelGroup = null
    if (page === 1 && !search) {
      try {
        const suggestions = await getIntelSuggestions(entityApiParam, { limit: 10 })
        if (suggestions?.length) {
          intelGroup = {
            label: '★ Frequently used',
            options: suggestions.map((entity) => ({
              ...toSelectOption(entity),
              __intelSource: entity.__intelSource,
            })),
          }
        }
      } catch (error) {
        // suggestions are best-effort — the normal list still loads
      }
    }

    const serverPromise = fetchClinicalEntitiesFromApi(
      entityApiParam,
      search,
      additional,
      page,
      ICD_PAGE_SIZE,
    ).then((records) => {
      if (records) {
        // write-through so the store warms up even before the background sync runs
        addOrUpdateClinicalEntitiesInIdb(entityApiParam, records).catch(() => {})
      }
      return records
    })

    let idbPageRecords = []
    let idbHasMore = false
    try {
      const matches = await searchClinicalEntitiesInIdb(entityApiParam, search, {
        disciplineSlugs: resolveToDisciplineSlugs(additional?.specializationId),
        leafOnly: Boolean(additional?.leafOnly),
      })
      idbPageRecords = matches.slice((page - 1) * ICD_PAGE_SIZE, page * ICD_PAGE_SIZE)
      idbHasMore = matches.length > page * ICD_PAGE_SIZE
    } catch (error) {
      console.warn(`IDB search failed for ${entityApiParam}; using server only`, error)
    }

    const serverRecords = await Promise.race([
      serverPromise.catch(() => null),
      new Promise((resolve) => setTimeout(() => resolve(null), CLINICAL_SERVER_MERGE_TIMEOUT_MS)),
    ])

    const alreadyLoadedIds = collectLoadedOptionIds(loadedOptions)
    // don't repeat an intel suggestion lower down in the block groups
    const intelIds = new Set((intelGroup?.options || []).map((option) => option?.value))
    const seenIds = new Set()
    const mergedRecords = []
    for (const record of [...idbPageRecords, ...(serverRecords || [])]) {
      const id = record?.id || record?.title
      if (!id || seenIds.has(id) || alreadyLoadedIds.has(id) || intelIds.has(id)) {
        continue
      }
      seenIds.add(id)
      mergedRecords.push(record)
    }

    const blockGroups = groupOptionsByIcdBlock(mergedRecords.map(toSelectOption))
    return {
      options: intelGroup ? [intelGroup, ...blockGroups] : blockGroups,
      hasMore: idbHasMore || (serverRecords || []).length >= ICD_PAGE_SIZE,
      additional: { ...(additional || {}), page: page + 1 },
    }
  }
}

/**
 * Descriptor suggestions for a selected clinical diagnosis: curated clinical_descriptors
 * whose relatedDiagnoses ICD codes cover the diagnosis' icd10Code (prefix match either way,
 * so F32.1 matches a descriptor tagged F32). IDB-first with one-shot API fallback.
 * Returns [] for clinic-curated diagnoses without an ICD code.
 */
export async function getSuggestedDescriptorsForDiagnosis(diagnosis, specializationId, limit = 12) {
  const icd10Code = (diagnosis?.icd10Code || diagnosis?.code || '').trim().toUpperCase()
  if (!icd10Code) return []

  let descriptors = []
  try {
    if (await clinicalEntityStoreHasData('clinical-descriptor')) {
      descriptors = await searchClinicalEntitiesInIdb('clinical-descriptor', '', {
        disciplineSlugs: resolveToDisciplineSlugs(specializationId),
      })
    }
  } catch (error) {
    console.warn('IDB descriptor lookup failed; falling back to API', error)
  }
  if (descriptors.length === 0) {
    const records = await fetchClinicalEntitiesFromApi(
      'clinical-descriptor',
      '',
      { specializationId },
      1,
      500,
    )
    if (records) {
      descriptors = records
      addOrUpdateClinicalEntitiesInIdb('clinical-descriptor', records).catch(() => {})
    }
  }

  const codeMatches = (relatedCode) => {
    const related = String(relatedCode || '').trim().toUpperCase()
    return related && (icd10Code.startsWith(related) || related.startsWith(icd10Code))
  }
  return descriptors
    .filter((descriptor) => (descriptor?.relatedDiagnoses || []).some(codeMatches))
    .sort(
      (a, b) =>
        String(a?.descriptorType || '').localeCompare(String(b?.descriptorType || '')) ||
        String(a?.title || '').localeCompare(String(b?.title || '')),
    )
    .slice(0, limit)
}

/**
 * Practitioner discipline for `specializationId`. Accepts the specializedCategories array off
 * the mentor profile (strings, or objects with id/slug); the backend bridges practitioner
 * nouns ("psychiatrist") to discipline slugs ("psychiatry") itself.
 */
export function getPrimarySpecializationId(doctorSpecializations) {
  if (typeof doctorSpecializations === 'string') return doctorSpecializations.trim() || null
  for (const item of asArray(doctorSpecializations)) {
    if (typeof item === 'string' && item.trim()) return item.trim()
    const slug = item?.slug || item?.id
    if (typeof slug === 'string' && slug.trim()) return slug.trim()
  }
  return null
}

const DefaultMenuList = reactSelectComponents.MenuList

/**
 * MenuList with a sticky "Most-specific codes only" toggle. Drive it by passing `icdLeafOnly`
 * (boolean) and `onIcdLeafOnlyChange` (fn) straight on the select — react-select exposes them
 * via selectProps. Toggling must be paired with cacheUniqs so options reload.
 */
function IcdMenuListWithLeafToggle(props) {
  const { icdLeafOnly, onIcdLeafOnlyChange } = props.selectProps || {}
  return (
    <DefaultMenuList {...props}>
      {typeof onIcdLeafOnlyChange === 'function' && (
        <div
          style={{
            position: 'sticky',
            top: '-4px',
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '7px 10px',
            margin: '-4px -4px 4px -4px',
            backgroundColor: '#ffffff',
            borderBottom: '1px solid rgba(139, 92, 246, 0.2)',
            cursor: 'pointer',
            fontSize: '12.5px',
            color: '#6b21a8',
            userSelect: 'none',
          }}
          // keep focus on the select input so the menu stays open while toggling
          onMouseDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onIcdLeafOnlyChange(!icdLeafOnly)
          }}
        >
          <input
            type="checkbox"
            checked={!!icdLeafOnly}
            readOnly
            style={{ accentColor: '#7c3aed', pointerEvents: 'none', margin: 0 }}
          />
          <span style={{ flex: 1, fontWeight: 600 }}>Most-specific codes only</span>
          <span style={{ opacity: 0.65 }}>uncheck for 3-char parents (e.g. F32)</span>
        </div>
      )}
      {props.children}
    </DefaultMenuList>
  )
}

export const IcdLeafToggleMenuList = wrapMenuList(IcdMenuListWithLeafToggle)

/** Section-header styling for the ICD block groups, merged into the select's `styles`. */
export const icdGroupStyles = {
  groupHeading: (provided) => ({
    ...provided,
    fontSize: '10.5px',
    fontWeight: 700,
    letterSpacing: '0.5px',
    color: '#7c3aed',
    backgroundColor: 'rgba(139, 92, 246, 0.07)',
    borderRadius: '4px',
    padding: '5px 8px',
    margin: '2px 0',
    textTransform: 'uppercase',
  }),
  group: (provided) => ({
    ...provided,
    padding: 0,
  }),
}

/**
 * Compact WHO-definition + exclusions summary for a selected clinical entity, for the detail
 * cards under the pickers. Renders nothing for clinic-curated entries without ICD data.
 */
export const IcdSelectedDetail = ({ entity, style }) => {
  const cls = entity?.icdClassification || {}
  const exclusions = asArray(cls?.exclusions)
  if (!entity?.description && exclusions.length === 0) return null
  return (
    <div style={{ fontSize: '12.5px', color: '#475569', lineHeight: 1.5, ...style }}>
      {entity?.description && (
        <div
          title={entity.description}
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {entity.description}
        </div>
      )}
      {exclusions.length > 0 && (
        <div style={{ marginTop: entity?.description ? '4px' : 0 }}>
          <b>Excludes:</b> {exclusions.join('; ')}
        </div>
      )}
    </div>
  )
}
